import * as Sentry from '@sentry/react';
import {
  DynamicPageVariant,
  ExperimentStats,
  VariantPrompt,
  Website,
} from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import {
  Clock,
  Copy,
  EditPencil,
  Flask,
  Medal,
  MoreVert,
  MultiplePages,
  OffTag,
  OnTag,
  PagePlus,
  Sparks,
  StatsUpSquare,
  Trash,
} from 'iconoir-react';
import { MouseEvent, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  duplicateVariant,
  duplicateVariantAsNewDynamicPage,
  duplicateWebsiteAsVariant,
  endExperiment,
  updateVariant,
} from '@/services/DynamicPageVariantsService';
import { useDynamicPageContext } from '../../../contexts/DynamicPageContext';
import { SnackbarContext } from '../../../contexts/SnackbarContext';
import { UserContext } from '../../../contexts/UserContext';

// Types
export type DynamicPageVariantsRowData =
  | {
      type: 'test';
      test: QueryDocumentSnapshot<Website>;
      variant: QueryDocumentSnapshot<DynamicPageVariant>;
      tests: QueryDocumentSnapshot<Website>[];
      testIndex: number;
    }
  | {
      type: 'variant';
      variant: QueryDocumentSnapshot<DynamicPageVariant>;
      tests: QueryDocumentSnapshot<Website>[];
    };

export interface GenerateParams {
  variantId: string;
  websiteId?: string;
  mode: 'variant' | 'test';
  /** For test mode: the control (first) website ID of the variant */
  controlWebsiteId?: string;
  /** The stored prompt from the variant (from agent conversation) */
  defaultPrompt?: VariantPrompt | null;
}

interface VariantRowActionsProps {
  original: DynamicPageVariantsRowData;
  experimentsStats?: Record<string, ExperimentStats>;
  onOpenEditor: (params: {
    websiteId: string;
    variantId: string;
    initialMessage?: string;
  }) => void;
  onGenerate?: (params: GenerateParams) => void;
  onEditNameAndDescription: (params: {
    variantId: string;
    initialName: string | undefined;
    initialDescription: string | undefined;
    initialPath: string | undefined;
  }) => void;
  onEditSchedule: (params: { variantId: string }) => void;
  onOpenExperimentModal: (params: {
    mode: 'create' | 'edit';
    variantId: string;
    variantName: string | undefined;
    experimentId?: string;
  }) => void;
  onOpenAnalyticsModal: (params: {
    websiteId: string;
    variantId: string;
  }) => void;
  onDeleteVariant: (variant: QueryDocumentSnapshot<DynamicPageVariant>) => void;
  onDeleteTest: (
    test: QueryDocumentSnapshot<Website>,
    variant: QueryDocumentSnapshot<DynamicPageVariant>,
  ) => void;
}

// Helper to check if row is in draft status
const isVariantDraft = (original: DynamicPageVariantsRowData): boolean => {
  return (
    original.type === 'variant' && original.variant.data().status === 'draft'
  );
};

// Helper to check if it's a single test variant
const isSingleTestVariant = (original: DynamicPageVariantsRowData): boolean => {
  return original.type === 'variant' && original.tests.length === 1;
};

// Helper to check if test needs content generation
const isTestDraft = (original: DynamicPageVariantsRowData): boolean => {
  if (original.type !== 'test') return false;
  const testData = original.test.data();
  return (
    testData.status === 'draft' ||
    !testData.development?.sections ||
    testData.development.sections.length === 0
  );
};

// Generate Button Component
function GenerateButton({
  onClick,
}: {
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
}) {
  return (
    <Button
      variant="default"
      className="font-inter h-7 text-[13px] font-medium -tracking-[0.01em]"
      size="sm"
      onClick={onClick}
    >
      <Sparks className="h-4 w-4" />
      Generate
    </Button>
  );
}

// Experiment Button Component
function ExperimentButton({
  onClick,
}: {
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
}) {
  return (
    <Button
      variant="outline"
      className="text-base-900 font-inter h-7 text-[13px] font-medium -tracking-[0.01em]"
      size="sm"
      onClick={onClick}
    >
      <Flask className="h-4 w-4" />
      Experiment
    </Button>
  );
}

// Edit Button Component
function EditButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      variant="outline"
      className="text-base-900 font-inter h-7 text-[13px] font-medium -tracking-[0.01em]"
      size="sm"
      onClick={onClick}
    >
      <EditPencil className="h-4 w-4" />
      Edit
    </Button>
  );
}

// Primary Actions (Generate/Experiment/Edit buttons)
function PrimaryActions({
  original,
  // experimentsStats,
  onOpenEditor,
  onGenerate,
  onOpenExperimentModal,
}: Pick<
  VariantRowActionsProps,
  | 'original'
  | 'experimentsStats'
  | 'onOpenEditor'
  | 'onGenerate'
  | 'onOpenExperimentModal'
>) {
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage } = useDynamicPageContext();
  // const navigate = useNavigate();
  // const { slug } = useParams();

  const isDraft = isVariantDraft(original);
  const isSingleTest = isSingleTestVariant(original);
  const testIsDraft = isTestDraft(original);

  // Show Generate button for single-test draft variants
  if (original.type === 'variant' && isSingleTest && isDraft) {
    return (
      <GenerateButton
        onClick={(e) => {
          e.stopPropagation();
          if (onGenerate) {
            onGenerate({
              variantId: original.variant.id,
              websiteId: original.tests[0]?.id,
              mode: 'variant',
              defaultPrompt: original.variant.data().prompt,
            });
          } else if (original.tests[0]?.id) {
            onOpenEditor({
              websiteId: original.tests[0].id,
              variantId: original.variant.id,
              initialMessage: 'Create a fully working page',
            });
          }
        }}
      />
    );
  }

  // Show Generate button for test rows with no content
  if (original.type === 'test' && testIsDraft) {
    const testDescription =
      original.test.data().name || original.test.data().description;
    // Get the control (first) website ID for copying from control
    const controlWebsiteId = original.tests[0]?.id;
    return (
      <GenerateButton
        onClick={(e) => {
          e.stopPropagation();
          if (onGenerate) {
            onGenerate({
              variantId: original.variant.id,
              websiteId: original.test.id,
              mode: 'test',
              controlWebsiteId,
              defaultPrompt: original.variant.data().prompt,
            });
          } else {
            onOpenEditor({
              websiteId: original.test.id,
              variantId: original.variant.id,
              initialMessage: testDescription
                ? `Update the page to test: ${testDescription}.`
                : 'Create a fully working page',
            });
          }
        }}
      />
    );
  }

  // Show Experiment button for single-test non-draft variants
  if (original.type === 'variant' && isSingleTest && !isDraft) {
    return (
      <>
        <ExperimentButton
          onClick={(e) => {
            e.stopPropagation();
            if (!selectedAccount || !selectedDynamicPage) return;
            onOpenExperimentModal({
              mode: 'create',
              variantId: original.variant.id,
              variantName: original.variant.data().name,
            });
          }}
        />
        <EditButton
          onClick={() => {
            if (original.tests[0]?.id) {
              onOpenEditor({
                websiteId: original.tests[0].id,
                variantId: original.variant.id,
              });
            }
          }}
        />
      </>
    );
  }

  // Show Experiment button for multi-test non-draft variants (active experiment)
  if (original.type === 'variant' && !isSingleTest && !isDraft) {
    return <></>;
    // const experimentId = original.variant.data().currentExperimentId;
    // const stats = experimentId ? experimentsStats?.[experimentId] : undefined;

    // // Determine button text and styling based on experiment status
    // const isWinnerFound = stats?.status === 'winner_found';
    // const isInconclusive = stats?.status === 'inconclusive';

    // const buttonText = isWinnerFound
    //   ? 'Winner Found'
    //   : isInconclusive
    //     ? 'Inconclusive'
    //     : 'Collecting Data';

    // const buttonClassName = isWinnerFound
    //   ? 'font-inter h-7 border-green-200 bg-green-50 text-[13px] font-medium text-green-700 -tracking-[0.01em] hover:bg-green-100'
    //   : isInconclusive
    //     ? 'font-inter h-7 border-amber-200 bg-amber-50 text-[13px] font-medium text-amber-700 -tracking-[0.01em] hover:bg-amber-100'
    //     : 'font-inter h-7 border-purple-200 bg-purple-50 text-[13px] font-medium text-purple-700 -tracking-[0.01em] hover:bg-purple-100';

    // const iconClassName = isWinnerFound
    //   ? 'h-4 w-4 text-green-600'
    //   : isInconclusive
    //     ? 'h-4 w-4 text-amber-600'
    //     : 'h-4 w-4 text-purple-600';

    // return (
    //   <Button
    //     variant="outline"
    //     className={buttonClassName}
    //     size="sm"
    //     onClick={(e) => {
    //       e.stopPropagation();
    //       // Navigate to experiments tab
    //       if (selectedDynamicPage) {
    //         navigate(
    //           `/workspace/${slug}/pages/${selectedDynamicPage.id}/experiments`,
    //         );
    //       }
    //     }}
    //   >
    //     <Flask className={iconClassName} />
    //     {buttonText}
    //     <NavArrowRight className="h-4 w-4" />
    //   </Button>
    // );
  }

  // Show Edit button for test rows that aren't drafts
  if (original.type === 'test' && !testIsDraft) {
    return (
      <EditButton
        onClick={() => {
          onOpenEditor({
            websiteId: original.test.id,
            variantId: original.variant.id,
          });
        }}
      />
    );
  }

  return null;
}

// Variant Dropdown Menu Items
function VariantMenuItems({
  original,
  onEditNameAndDescription,
  onEditSchedule,
  onOpenExperimentModal,
  onOpenAnalyticsModal,
  onDeleteVariant,
}: Pick<
  VariantRowActionsProps,
  | 'original'
  | 'onEditNameAndDescription'
  | 'onEditSchedule'
  | 'onOpenExperimentModal'
  | 'onOpenAnalyticsModal'
  | 'onDeleteVariant'
>) {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { show, hide } = useContext(SnackbarContext);
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage } = useDynamicPageContext();

  // Early return check moved after hooks
  if (original.type !== 'variant') return null;

  const variantData = original.variant.data();
  const isActive = variantData.status === 'active';
  const isDraft = variantData.status === 'draft';
  const hasMultipleTests = original.tests.length > 1;
  const variantId = original.variant.id;

  const handleToggleStatus = () => {
    if (!selectedAccount || !selectedDynamicPage) return;
    updateVariant(selectedAccount.id, selectedDynamicPage.id, variantId, {
      status: isActive ? 'disabled' : 'active',
    });
  };

  const handleDuplicate = async () => {
    if (!selectedAccount || !selectedDynamicPage) return;

    const sb = show('Duplicating variant...', 'info', {
      disableAutoClose: true,
    });

    try {
      await duplicateVariant(
        selectedAccount.id,
        selectedDynamicPage.id,
        variantId,
      );
      show('Variant duplicated', 'info');
    } catch (e) {
      console.error(e);
      show('Failed to duplicate variant', 'warn');
      Sentry.captureException(e);
    } finally {
      hide(sb.id);
    }
  };

  const handleDuplicateAsNewPage = async () => {
    if (!selectedAccount || !selectedDynamicPage) return;

    const sb = show('Duplicating variant as new page...', 'info', {
      disableAutoClose: true,
    });

    try {
      const res = await duplicateVariantAsNewDynamicPage(
        selectedAccount.id,
        selectedDynamicPage.id,
        variantId,
      );
      show('Variant duplicated', 'info');
      navigate(`/workspace/${slug}/pages/${res.campaignId}/details`);
    } catch (e) {
      console.error(e);
      show('Failed to duplicate variant', 'warn');
      Sentry.captureException(e);
    } finally {
      hide(sb.id);
    }
  };

  return (
    <>
      <DropdownMenuItem
        onClick={() => {
          onEditNameAndDescription({
            variantId: original.variant.id,
            initialName: variantData.name ?? undefined,
            initialDescription: variantData.description ?? undefined,
            initialPath: variantData.path ?? undefined,
          });
        }}
      >
        <EditPencil className="h-4 w-4" />
        Edit Name & Description
      </DropdownMenuItem>

      <DropdownMenuItem
        onClick={() => onEditSchedule({ variantId: original.variant.id })}
      >
        <Clock className="h-4 w-4" />
        Schedule
      </DropdownMenuItem>

      {!isDraft && (
        <>
          <DropdownMenuItem onClick={handleToggleStatus}>
            {isActive ? (
              <OffTag className="h-4 w-4" />
            ) : (
              <OnTag className="h-4 w-4" />
            )}
            {isActive ? 'Deactivate Variant' : 'Activate Variant'}
          </DropdownMenuItem>

          <DropdownMenuItem onClick={handleDuplicate}>
            <Copy className="h-4 w-4" />
            Duplicate Variant
          </DropdownMenuItem>

          <DropdownMenuItem onClick={handleDuplicateAsNewPage}>
            <PagePlus className="h-4 w-4" />
            Duplicate Variant as New Dynamic Page
          </DropdownMenuItem>

          {hasMultipleTests ? (
            <DropdownMenuItem
              onClick={() => {
                const experimentId = variantData.currentExperimentId;
                if (!experimentId) return;
                onOpenExperimentModal({
                  mode: 'edit',
                  variantId: original.variant.id,
                  variantName: variantData.name,
                  experimentId,
                });
              }}
            >
              <Flask className="h-4 w-4" />
              Edit Experiment
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={() => {
                onOpenAnalyticsModal({
                  websiteId: original.tests[0].id,
                  variantId: original.variant.id,
                });
              }}
            >
              <StatsUpSquare className="h-4 w-4" />
              View Analytics
            </DropdownMenuItem>
          )}
        </>
      )}

      <DropdownMenuItem
        variant="destructive"
        onClick={() => onDeleteVariant(original.variant)}
        className="text-red-600 hover:bg-red-50"
      >
        <Trash className="h-4 w-4" />
        Delete Variant
      </DropdownMenuItem>
    </>
  );
}

// Test (Experiment) Dropdown Menu Items
function TestMenuItems({
  original,
  onOpenExperimentModal,
  onOpenAnalyticsModal,
  onDeleteTest,
}: Pick<
  VariantRowActionsProps,
  'original' | 'onOpenExperimentModal' | 'onOpenAnalyticsModal' | 'onDeleteTest'
>) {
  const { show, hide } = useContext(SnackbarContext);
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage } = useDynamicPageContext();

  // Early return check moved after hooks
  if (original.type !== 'test') return null;

  const variantData = original.variant.data();
  const isDraft = variantData.status === 'draft';
  const experimentId = variantData.currentExperimentId;
  const websiteIdsCount = variantData.websiteIds.length;
  const variantId = original.variant.id;
  const testId = original.test.id;

  const handleEndExperiment = async () => {
    if (!selectedAccount || !selectedDynamicPage) return;

    const sb = show('Ending experiment...', 'info', { disableAutoClose: true });

    try {
      await endExperiment(
        selectedAccount.id,
        selectedDynamicPage.id,
        variantId,
        testId,
      );
      show('Experiment ended', 'info');
    } catch (e) {
      console.error(e);
      show('Failed to end experiment', 'warn');
      Sentry.captureException(e);
    } finally {
      hide(sb.id);
    }
  };

  const handleDuplicateAsVariant = async () => {
    if (!selectedAccount || !selectedDynamicPage) return;

    const sb = show('Duplicating experiment as another variant...', 'info', {
      disableAutoClose: true,
    });

    try {
      await duplicateWebsiteAsVariant(
        selectedAccount.id,
        selectedDynamicPage.id,
        variantId,
        testId,
      );
      show('Experiment duplicated as a new variant', 'info');
    } catch (e) {
      console.error(e);
      show('Failed to duplicate', 'warn');
      Sentry.captureException(e);
    } finally {
      hide(sb.id);
    }
  };

  const handleDeleteExperiment = async () => {
    if (!selectedAccount || !selectedDynamicPage) return;

    // If there are only 2 tests (original + 1 experiment), use endExperiment
    // Otherwise use updateExperiment to remove the test
    if (websiteIdsCount === 2) {
      const sb = show('Deleting experiment...', 'info', {
        disableAutoClose: true,
      });

      try {
        await endExperiment(
          selectedAccount.id,
          selectedDynamicPage.id,
          variantId,
          testId,
        );
        show('Experiment deleted', 'info');
      } catch (e) {
        console.error(e);
        show('Failed to delete experiment', 'warn');
        Sentry.captureException(e);
      } finally {
        hide(sb.id);
      }
    } else {
      onDeleteTest(original.test, original.variant);
    }
  };

  return (
    <>
      <DropdownMenuItem
        onClick={() => {
          if (experimentId) {
            onOpenExperimentModal({
              mode: 'edit',
              variantId: variantId,
              variantName: variantData.name,
              experimentId,
            });
          }
        }}
      >
        <EditPencil className="h-4 w-4" />
        Edit Experiment
      </DropdownMenuItem>

      {!isDraft && (
        <>
          <DropdownMenuItem onClick={handleEndExperiment}>
            <Medal className="h-4 w-4" />
            Mark as Winner (End Experiment)
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => {
              onOpenAnalyticsModal({
                websiteId: testId,
                variantId: variantId,
              });
            }}
          >
            <StatsUpSquare className="h-4 w-4" />
            View Analytics
          </DropdownMenuItem>

          <DropdownMenuItem onClick={handleDuplicateAsVariant}>
            <MultiplePages className="h-4 w-4" />
            Duplicate as another Variant
          </DropdownMenuItem>

          {websiteIdsCount > 2 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                onClick={() => onDeleteTest(original.test, original.variant)}
              >
                <Trash className="h-4 w-4" />
                Remove Variation From Experiment
              </DropdownMenuItem>
            </>
          )}
        </>
      )}

      {isDraft && (
        <DropdownMenuItem
          variant="destructive"
          onClick={handleDeleteExperiment}
          className="text-red-600 hover:bg-red-50"
        >
          <Trash className="h-4 w-4" />
          Delete Experiment
        </DropdownMenuItem>
      )}
    </>
  );
}

// Main Component
export function VariantRowActions({
  original,
  experimentsStats,
  onOpenEditor,
  onGenerate,
  onEditNameAndDescription,
  onEditSchedule,
  onOpenExperimentModal,
  onOpenAnalyticsModal,
  onDeleteVariant,
  onDeleteTest,
}: VariantRowActionsProps) {
  return (
    <div className="flex w-full flex-row items-center justify-end gap-2">
      <PrimaryActions
        original={original}
        experimentsStats={experimentsStats}
        onOpenEditor={onOpenEditor}
        onGenerate={onGenerate}
        onOpenExperimentModal={onOpenExperimentModal}
      />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon">
            <MoreVert className="text-base-900 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {original.type === 'variant' ? (
            <VariantMenuItems
              original={original}
              onEditNameAndDescription={onEditNameAndDescription}
              onEditSchedule={onEditSchedule}
              onOpenExperimentModal={onOpenExperimentModal}
              onOpenAnalyticsModal={onOpenAnalyticsModal}
              onDeleteVariant={onDeleteVariant}
            />
          ) : (
            <TestMenuItems
              original={original}
              onOpenExperimentModal={onOpenExperimentModal}
              onOpenAnalyticsModal={onOpenAnalyticsModal}
              onDeleteTest={onDeleteTest}
            />
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
